import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";

import { StyledPaper } from "../Base";
import { useCertificateForm, type SubmitPayload } from "./useCertificateForm";
import { CertificateDetailsForm } from "./CertificateDetailsForm";

// ============= Types =============

export interface SingleCertificateUploadProps {
  crewId: number;
  courseId: number;
  courseName: string;
  isCustom: boolean;
  validityPeriodMonths: number | null;
  userId: number;
  onSubmit: (values: SubmitPayload) => Promise<string | null>;
}

// ============= Component =============

const SingleCertificateUpload = ({
  crewId,
  courseId,
  courseName,
  isCustom,
  validityPeriodMonths,
  userId,
  onSubmit,
}: SingleCertificateUploadProps) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const fixedCourse = { id: courseId, isCustom, validityPeriodMonths };
  const fixedUserIds = [userId];

  const { form, validityHook, neverExpires, periodsResult, getTooltipContent } = useCertificateForm({
    crewId,
    fixedCourse,
    fixedUserIds,
  });

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isValid },
  } = form;

  useEffect(() => {
    validityHook.fetchPeriods();
  }, [courseId, userId]);

  const submit = async (values: SubmitPayload) => {
    const error = await onSubmit(values);
    if (error) {
      enqueueSnackbar(error, { variant: "error" });
    }
  };

  const courseLabel = `(${isCustom ? `cc_${courseId}` : courseId}) ${courseName}`;

  return (
    <StyledPaper variant="outlined">
      <Box
        component="form"
        onSubmit={handleSubmit(submit)}
        sx={{
          p: 2,
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        <CertificateDetailsForm
          control={control}
          neverExpires={neverExpires}
          showWarningIcon={!!periodsResult.divergentPeriods}
          getTooltipContent={() => getTooltipContent(t)}
          courseLabel={courseLabel}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Button type="submit" variant="contained" disabled={!isValid || isSubmitting}>
            {t("upload_certificate_in_cloud")}
          </Button>
        </Box>
      </Box>
    </StyledPaper>
  );
};

export default SingleCertificateUpload;
